/**
 * THE SIGN-IN PAGE IN THE NEW DESIGN (PV5-8:B, design-system §7b; surfaces.md, sign in) — prest0.ai/prest0design/auth.
 *
 * The pavilion with one standing object: the frosted card on the horizon, reflected in the water. The welcome line
 * lies on the ground at the left; in the invitation-code journey the journey's steps stand there instead, as on today's
 * page. The header is the focused one (no page links, no footer), and its one action is the mode switch, the gold
 * underline ("create an account").
 *
 * The same page as today's src/pages/AuthPage.tsx, word for word, with the same behaviour, because both run the same
 * hooks: how the visit arrived (src/hooks/useAuthEntry.ts) and the form (src/hooks/useSignInForm.ts). Loaded on demand,
 * in its own chunk (`pavilion-auth`); today's page is its fallback.
 */
import React from 'react';
import SeoHead from '../../../../components/SeoHead';
import { AUTH_PAGE_WORDS, SIGN_IN_WORDS as W } from '../../../../components/auth/signInWords';
import { NEW_ACCOUNT_JOURNEY, journeyCounter } from '../../../../config/newAccountJourney';
import { useAuthEntry } from '../../../../hooks/useAuthEntry';
import { useSignInForm } from '../../../../hooks/useSignInForm';
import { usePageSheet } from '../../shell/pavilionStyles';
import { useFocusedAction } from '../../shell/shellContext';
// The stage by its own file, never through '../../kit': see kit/stage/Stage.tsx.
import { Stage, StandingCard } from '../../kit/stage/Stage';
import { Notice } from '../../kit/feedback/Notice';
import { AUTH_CSS, AUTH_STYLE_ID } from './authCss';
import { SignInCard, SignInCardPicture } from './SignInCard';

const WELCOME_ID = 'pv-auth-welcome';
const CARD_TITLE_ID = 'pv-auth-card-title';

/** The invitation-code journey, where the welcome line stands on a plain visit: the steps, where this one is, the note. */
const JourneyOnTheGround: React.FC = () => {
  const current = NEW_ACCOUNT_JOURNEY.findIndex((s) => s.key === 'account');
  const step = NEW_ACCOUNT_JOURNEY[current];
  return (
    <div className="pv-auth-journey" data-testid="journey-stepper">
      <p className="pv-eyebrow">{journeyCounter(current + 1, NEW_ACCOUNT_JOURNEY.length)}</p>
      <ol className="pv-auth-steps">
        {NEW_ACCOUNT_JOURNEY.map((s, i) => (
          <li key={s.key} data-state={i < current ? 'done' : i === current ? 'active' : 'upcoming'} aria-current={i === current ? 'step' : undefined}>
            <span className="pv-auth-step-dot" aria-hidden="true">{i < current ? '✓' : i + 1}</span>
            <span className="pv-auth-step-label">{s.label}</span>
          </li>
        ))}
      </ol>
      {step?.timeHint && (
        <p className="pv-auth-step-hint">
          {step.label} — <span className="pv-auth-step-time">{step.timeHint}</span>
        </p>
      )}
      <p className="pv-stage-note">{AUTH_PAGE_WORDS.codeSaved}</p>
    </div>
  );
};

const PavilionAuth: React.FC = () => {
  usePageSheet(AUTH_STYLE_ID, AUTH_CSS);
  const { returnUrl, initialMode, loginHint, codeJourney } = useAuthEntry();
  const form = useSignInForm({ redirectPath: returnUrl, initialMode });

  // The header's one action is the mode switch, while the form is what the card shows.
  const formShows = form.authStage === 'idle' && !form.signedInConfirmed;
  useFocusedAction(formShows ? (form.isSignUp ? W.switchMode.toLogin : W.switchMode.toSignup) : null, form.toggleMode);

  const titleLevel = codeJourney ? 'h1' : 'h2';
  const cardTitle = form.isSignUp ? W.mode.signup : W.mode.login;

  return (
    <>
      <SeoHead tabTitle={AUTH_PAGE_WORDS.seo.tabTitle} title={AUTH_PAGE_WORDS.seo.title} description={AUTH_PAGE_WORDS.seo.description} noIndex />
      <Stage
        labelledBy={codeJourney ? CARD_TITLE_ID : WELCOME_ID}
        object={
          <StandingCard mirror={<SignInCardPicture title={cardTitle} />}>
            {loginHint && (
              <div className="pv-auth-hint">
                <Notice tone="info">{loginHint}</Notice>
              </div>
            )}
            <SignInCard form={form} redirectPath={returnUrl} titleLevel={titleLevel} titleId={CARD_TITLE_ID} />
          </StandingCard>
        }
      >
        {codeJourney ? (
          <JourneyOnTheGround />
        ) : (
          // The welcome line lies on the ground at the left (surfaces.md): today's heading, "AI" keeping its capitals.
          <h1 className="pv-auth-welcome" id={WELCOME_ID}>
            {W.heading.lead}
            <span className="pv-auth-ai">{W.heading.ai}</span>
            {W.heading.tail}
          </h1>
        )}
      </Stage>
    </>
  );
};

export default PavilionAuth;
